// BUILD-046 task 8.17, rebuilt in correction cycle 1bb88b3e (stage B2a, items F1 on the screen, N1 and N9): the Team tab - the Oversight_View for an
// Authorized_Overseer.
//
// What it shows. The OPEN list (default) is the caller's oversight scope from `GET /work/oversight`; the COMPLETED list (the Open / Completed choice) is
// the same scope read page by page from `GET /work/oversight?status=completed`, newest first, with Load more. The tab is labelled with its scope
// ("Team (your direct reports)" or "Company", from `companyOversight`). A task seen here is never added to the viewer's own lists or counts (Req 7.7, 14.19),
// and Done and complete-on-behalf are never offered.
//
// Rows are the same compact row as Mine and Shared (TaskRow) and emphasise the assignee, the Task due date and the status. Reopen is a visible button on an
// ORDINARY completed row (the existing action behind the existing confirmation); a source-governed completed row shows the explanation instead of a working
// Reopen. Every other action (Assign to…, Recovery assignment…, Reassign…, Return to queue) is in the row's one "More actions" menu. Source and evidence links
// resolve with the VIEWER's own authorization (Req 14.17); the owner-left text is the server's, exactly as given.
//
// The data and the filters (department, search, sort) are the host's; the host passes `derive` (the department filter, the search and the sort over the
// loaded tasks). This file draws them.
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { WorkTask } from '../../api/workShared';
import { describeList } from './listStateModel';
import type { ListState } from './listStateModel';
import type { PagedView } from './pagedListModel';
import { DEPARTMENTS, describeOversightRow, oversightTitle } from './oversightViewModel';
import type { LinkView, OversightItem, OversightRowView } from './oversightViewModel';
import { OVERSIGHT_FILTER_OPTIONS, reopenOffer } from './oversightCompletedModel';
import type { OversightFilter } from './oversightCompletedModel';
import { formatDueDate, relativeAge } from './taskRowModel';
// BUILD-046 section C: a row names its attention condition (a missed follow-up, overdue, a follow-up today, due today) by the same rule as the Needs attention sort, and Comfortable shows the
// next follow-up date and the latest progress note, both within what the viewer already sees (a row in its minimal form shows none).
import { attentionText, followUpDueOn } from './attentionModel';
import { latestUpdateLine } from './updateTaskModel';
// Overdue (Owner-approved 2026-10-20, one rule for every row since 2026-10-21): a row reads Overdue by THE one rule (overdueRule.ts) - an open, live, fully shown task with a Task
// due date before the Phoenix day, or the server's flag - judged against the page's one Phoenix-day signal (usePhoenixDay). "Not yet taken" and claimed rows alike.
import { isOverdueTask } from './overdueRule';
import { dueCellStatesOverdue } from './dueLabel';
import { usePhoenixDay } from './usePhoenixDay';
import { ActionOutcomeNotices } from './ActionOutcomeNotices';
import { presentIdsOf } from './actionOutcomeMemory';
import { ListErrorBlock, ListStateView } from './ListStateView';
import { OriginChip, hasVisibleOrigin } from './OriginChip';
import { RequesterBadge } from './RequesterBadge';
import { badgeForTask } from './requesterBadgeModel';
import { comfortableRequesterShown, showRequesterComfortable } from './showRequesterStore';
import { ActionsCell, Cell, DueMark, PriorityMark, TaskCell, TaskTable, TaskTableRow, quietLineOf } from './TaskTable';
import { jobOf } from './jobDisplayModel';
import { customerOf } from './customerDisplayModel';
import { associationParts } from './CustomerParts';
import { useCustomersInPlay } from './useCustomerColumn';
// D1 display cleanup: the "(FICTIONAL)" test marker is not shown. Increment 1b-i: the assignee and the people a task was sent to are INITIALS CIRCLES in the Assigned to cell (the full
// name on hover, keyboard focus and tap: PersonAvatar.tsx); a group stays text, in the Task cell's quiet "To <group>".
import { AvatarGroup } from './PersonAvatar';
import { TaskActionControls } from './TaskActionControls';
import { TaskHistoryPanel } from './TaskHistoryPanel';
import { taskEntries, taskEntryOffers } from './taskActionOffers';
import type { TaskEntryKind } from './taskActionOffers';
import type { MenuEntry } from './RowMoreMenu';
import type { ActionMe } from './oversightActionsModel';
import type { Density } from './densityStore';

type TeamMe = ActionMe & { companyOversight?: boolean };

// The tab's name and scope (Team or Company) are pageTabsModel's; its Status (Open / Completed) and Filter (Department) controls are the page toolbar's.

function Link({ link }: { link: LinkView }) {
  if (link.kind === 'link') {
    return (
      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-[#2a7fb8] underline">
        {link.label}
        {link.simulated && <span className="ml-1 text-[10px] font-semibold text-amber-800">SIMULATED</span>}
      </a>
    );
  }
  if (link.kind === 'text') {
    return (
      <span className="text-gray-700">
        {link.label}
        {link.simulated && <span className="ml-1 text-[10px] font-semibold text-amber-800">SIMULATED</span>}
      </span>
    );
  }
  // not authorized for this viewer: not activatable, and it names nothing
  return (
    <span className="text-gray-400 italic" aria-disabled="true">
      {link.label}
    </span>
  );
}

/**
 * The "Assigned to" cell (increment 1b-i): circles, and no name beside them. A claimed task is its owner's circle; a task nobody has taken shows the people it was sent to as circles and the
 * words "Not yet taken" (a group it was sent to is in the Task cell's quiet "To <group>"). A task that names no owner keeps the words ("Unknown").
 */
function AssignedToCell({ row, density }: { row: OversightRowView; density: Density }) {
  const compact = density === 'compact';
  const size = compact ? 'compact' : 'comfortable';
  if (row.ownerPerson !== null) return <AvatarGroup people={[row.ownerPerson]} size={size} label="Assigned to" />;
  if (row.assigneeName !== null) return <>{row.assigneeName}</>;
  return (
    // Team table cleanup: the Assigned to column is narrow, so in BOTH densities the circles come first and the words "Not yet taken" wrap beneath them (nothing is clipped); the circles
    // themselves are unchanged (Compact shows two and "+N", Comfortable three and "+N", each with its full name on hover, focus and tap).
    <span className={compact ? 'flex flex-wrap items-center gap-x-1 gap-y-0.5' : 'flex flex-wrap items-center gap-x-1.5 gap-y-1'} data-testid="team-unclaimed-cell">
      <AvatarGroup people={row.recipientPeople} size={size} max={compact ? 2 : 3} label="Sent to" />
      <span className="min-w-0 text-xs leading-tight text-gray-600" title="Not yet taken">
        Not yet taken
      </span>
    </span>
  );
}

interface RowProps {
  /** The table's variant (the same one its TaskTable has), so the row and its headings share one grid: Completed has room for Reopen beside the "..." menu. */
  variant: 'team' | 'team-completed';
  item: WorkTask;
  me: TeamMe | null;
  adapterSimulated: boolean;
  density: Density;
  now: number;
  onActionDone: () => void;
  onTaskEntry?: (kind: TaskEntryKind, target: { taskId: string; requestId: string; title: string }) => void;
}

function TeamRow({ variant, item, me, adapterSimulated, density, now, onActionDone, onTaskEntry }: RowProps) {
  const phoenixDay = usePhoenixDay();
  // CHANGED 2026-10-25: while customers are in play, a row with a job and no customer writes "Job: <name>" (associationParts), so a job never reads as a customer.
  const customersInPlay = useCustomersInPlay();
  const row = describeOversightRow(item as unknown as OversightItem, now, me);
  const offer = row.status === 'completed' ? reopenOffer(me, item) : { kind: 'none' as const };
  const completedAt = typeof item.completedAt === 'string' ? relativeAge(item.completedAt, now) : '';
  const compact = density === 'compact';
  // Lines Comfortable draws under the row (Compact has them inline): the owner-left text, the source and evidence links and the governed-Reopen sentence.
  const hasStaticLines = row.ownerLeft.length > 0 || row.source !== null || row.evidence.length > 0 || offer.kind === 'governed';
  // The requester is shown whenever one is recorded (the rule `requesterText` always had): now the short badge, "By Julie · Oct 18".
  const badge = badgeForTask(item, row.requesterText !== null && comfortableRequesterShown(density, showRequesterComfortable.get()));
  // The task's History (read only; the server decides who may read it). A task shown in its minimal form (restricted source) offers none.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Section A: Edit task, Hand off and History by the same rules as Assigned by Me and My Tasks (taskActionOffers.ts). Only the owner of an open task is offered Edit task and Hand off; the server decides.
  const target = { taskId: item.id, requestId: '', title: row.title };
  // Team table cleanup: when the Due column already writes the word "Overdue" above the date, the quiet line leaves the literal word out (a follow-up warning and "Due today" are kept).
  const overdueNow = isOverdueTask(item, phoenixDay);
  const dueStatesOverdue = !row.minimal && dueCellStatesOverdue({ dueDate: item.dueDate, overdue: overdueNow }, phoenixDay);
  const attention = row.minimal ? null : attentionText(item, phoenixDay, (date) => formatDueDate(date) ?? date, { omitOverdue: dueStatesOverdue });
  const followLabel = row.minimal ? null : formatDueDate(item.followUpDate);
  const latestLine = row.minimal ? null : latestUpdateLine(item, (date) => formatDueDate(date) ?? date);
  const historyEntries: MenuEntry[] = row.minimal ? [] : taskEntries(taskEntryOffers(me, item), row.title, { onUpdate: () => onTaskEntry?.('update', target), onEdit: () => onTaskEntry?.('edit', target), onHandoff: () => onTaskEntry?.('handoff', target), onHistory: () => setHistoryOpen((open) => !open) }).filter((entry) => entry.key === 'history' || onTaskEntry !== undefined);
  return (
    <TaskActionControls me={me} row={item} view="oversight" onDone={() => onActionDone()} extraEntries={historyEntries}>
      {({ trigger, panel }) => (
        <TaskTableRow
          variant={variant}
          id={`oversight-${row.id}`}
          density={density}
          requested={badge}
          job={jobOf(item)}
          customer={customerOf(item)}
          jobFirst
          below={
            // Increment 1a (D): Compact is ONE tight row, so the owner-left text, the source and evidence links and the governed-Reopen sentence are inline in the row there (the task cell's
            // quiet part); Comfortable keeps them as lines under the row. Panels the person opens (History, actions) are still below in both.
            (!compact && hasStaticLines) || (historyOpen && !row.minimal) || panel !== null ? (
              <>
                {!compact && row.ownerLeft.length > 0 && (
                  <ul className="mt-1 text-xs text-amber-800 space-y-0.5">
                    {row.ownerLeft.map((text) => (
                      <li key={text}>{text}</li>
                    ))}
                  </ul>
                )}
                {!compact && (row.source !== null || row.evidence.length > 0) && (
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
                    {row.source !== null && <Link link={row.source} />}
                    {row.evidence.map((link, index) => (
                      <Link key={`${link.label}-${index}`} link={link} />
                    ))}
                  </div>
                )}
                {!compact && offer.kind === 'governed' && (
                  <p className="mt-1 text-xs text-gray-600" data-testid="governed-reopen-text">
                    {offer.text}
                  </p>
                )}
                {historyOpen && !row.minimal && <TaskHistoryPanel taskId={row.id} title={row.title} onClose={() => setHistoryOpen(false)} />}
                {panel}
              </>
            ) : undefined
          }
        >
          <TaskCell
            title={row.title}
            density={density}
            secondaryBelow
            badge={badge !== null ? <RequesterBadge badge={badge} /> : null}
            secondary={
              quietLineOf([
                  // BUILD-046 J1a: the Job, then the Customer (Team table cleanup: job first, each labelled), are the FIRST parts in Comfortable; Compact has its own column.
                  ...associationParts(item, density, { jobFirst: true, customersInPlay }),
                  attention !== null ? <span key="attention" className="font-medium text-amber-800" data-testid="attention-mark">{attention}</span> : null,
                  // BUILD-046 G4: Compact draws no detail lines, so a follow-up that is due carries its next action here (Comfortable has it in the detail line).
                  compact && !row.minimal && row.nextAction !== null && followUpDueOn(item, phoenixDay) ? <span key="next-action" title={`Next action: ${row.nextAction}`} data-testid="row-next-action">{`Next: ${row.nextAction}`}</span> : null,
                  row.project,
                  // A group is text; the people it was sent to are the circles of the Assigned to cell (their names are not written twice).
                  row.recipientGroupText !== null ? `To ${row.recipientGroupText}` : null,
                  row.status === 'completed' && completedAt !== '' ? `Completed ${completedAt}` : null,
                  row.reassignmentNeeded ? 'Reassignment needed' : null,
                  hasVisibleOrigin(item) ? <OriginChip key="origin" item={item} adapterSimulated={adapterSimulated} /> : null,
                  // Compact only: what Comfortable draws as lines under the row, as the last parts of the one line.
                  compact && row.ownerLeft.length > 0 ? (
                    <span key="owner-left" className="text-amber-800" title={row.ownerLeft.join(' ')}>
                      {row.ownerLeft.join(' ')}
                    </span>
                  ) : null,
                  compact && row.source !== null ? <Link key="source" link={row.source} /> : null,
                  ...(compact ? row.evidence.map((link, index) => <Link key={`evidence-${link.label}-${index}`} link={link} />) : []),
                  compact && offer.kind === 'governed' ? (
                    <span key="governed" title={offer.text}>
                      {offer.text}
                    </span>
                  ) : null,
              ])
            }
            detail={
              row.nextAction !== null || row.waitingOn !== null || followLabel !== null || latestLine !== null ? (
                <span className="block space-y-0.5">
                  {(row.nextAction !== null || row.waitingOn !== null || followLabel !== null) && (
                    <span className="flex flex-wrap gap-x-3">
                      {row.nextAction !== null && <span>Next action: {row.nextAction}</span>}
                      {followLabel !== null && <span data-testid="team-follow-up">Next follow-up date: {followLabel}</span>}
                      {row.waitingOn !== null && <span>Waiting on {row.waitingOn}</span>}
                    </span>
                  )}
                  {latestLine !== null && (
                    <span className="block truncate" title={latestLine.text} data-testid="team-latest-update">
                      {latestLine.label}: {latestLine.text}
                    </span>
                  )}
                </span>
              ) : undefined
            }
          />
          {/* 1b-i: initials circles, no name beside them. The full name is on hover, keyboard focus and tap; one tab stop per circle. */}
          <Cell stackedLabel="Assigned to">
            <AssignedToCell row={row} density={density} />
          </Cell>
          <Cell stackedLabel="Priority">
            <PriorityMark priority={row.priority} />
          </Cell>
          <Cell stackedLabel="Due">
            <DueMark dueDate={row.minimal ? null : item.dueDate} overdue={overdueNow} stack tight />
          </Cell>
          <Cell stackedLabel="Status">
            <span className="whitespace-nowrap capitalize">{row.status === 'in_progress' ? 'in progress' : row.status}</span>
          </Cell>
          <ActionsCell>{trigger}</ActionsCell>
        </TaskTableRow>
      )}
    </TaskActionControls>
  );
}

export interface TeamViewProps {
  filter: OversightFilter;
  /** The open list (`oversight` list of the host's lists controller). */
  open: ListState<WorkTask>;
  /** The completed list, described (oversightCompletedModel.describeCompleted). */
  completed: PagedView<WorkTask>;
  /** The rows to draw: the loaded tasks of the chosen list after the department filter, the search and the sort (viewListModel). */
  rows: readonly WorkTask[];
  /** True while a department or a search narrows the list (so an empty result says so). */
  narrowed: boolean;
  /** Shown instead of the usual empty line when a Customer / Job or Customer filter is on and leaves no row (it names the filter and offers to clear it). */
  emptyFilterNote?: ReactNode;
  me: TeamMe | null;
  adapterSimulated: boolean;
  density: Density;
  onRetryOpen: () => void;
  onRetryCompleted: () => void;
  onLoadMore: () => void;
  /** After an ACCEPTED action: the host reloads the lists the change touched (the open list and the completed list). */
  onActionDone: () => void;
  onTaskEntry?: (kind: TaskEntryKind, target: { taskId: string; requestId: string; title: string }) => void;
}

export function TeamView({ filter, open, completed, rows, narrowed, emptyFilterNote, me, adapterSimulated, density, onRetryOpen, onRetryCompleted, onLoadMore, onActionDone, onTaskEntry }: TeamViewProps) {
  const [now] = useState(() => Date.now());
  const title = oversightTitle(me);
  const tableVariant = filter === 'completed' ? 'team-completed' : 'team';
  const rowsOf = (emptyText: string) => {
    const shown = rows;
    if (shown.length === 0 && emptyFilterNote) return <>{emptyFilterNote}</>;
    if (shown.length === 0) return <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">{narrowed ? 'No tasks match the current department or search.' : emptyText}</div>;
    return (
      <TaskTable variant={tableVariant} ariaLabel={`${title}: ${filter === 'completed' ? 'completed tasks' : 'open tasks'}`}>
        {shown.map((item) => (
          <TeamRow key={item.id} variant={tableVariant} item={item} me={me} adapterSimulated={adapterSimulated} density={density} now={now} onActionDone={onActionDone} onTaskEntry={onTaskEntry} />
        ))}
      </TaskTable>
    );
  };

  return (
    <div data-testid="oversight-view">
      <ActionOutcomeNotices presentIds={filter === 'completed' ? (completed.kind === 'data' ? completed.items.map((t) => t.id) : null) : presentIdsOf(open)} scope="oversight" />

      {filter === 'open' ? (
        <ListStateView view={describeList(open, 'Team Work', 'There is no team work to show.')} listLabel="Team Work" onRetry={onRetryOpen} renderItems={() => rowsOf('There is no team work to show.')} />
      ) : (
        <>
          {completed.kind === 'loading' && (
            <div className="py-8 text-center text-sm text-gray-500" role="status" aria-live="polite">
              {completed.text}
            </div>
          )}
          {completed.kind === 'unavailable' && (
            <p role="status" className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600" data-testid="completed-unavailable">
              {completed.text}
            </p>
          )}
          {completed.kind === 'error' && <ListErrorBlock heading={completed.heading} detail={completed.detail} onRetry={onRetryCompleted} listLabel="completed team work" />}
          {completed.kind === 'empty' && <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">{completed.text}</div>}
          {completed.kind === 'data' && (
            <>
              {rowsOf('No completed team work to show.')}
              {completed.moreError !== null && (
                <p role="alert" className="mt-2 text-xs text-red-700">
                  More completed tasks could not be loaded: {completed.moreError}
                </p>
              )}
              {(completed.canLoadMore || completed.loadingMore) && (
                <button
                  type="button"
                  disabled={completed.loadingMore}
                  onClick={onLoadMore}
                  className="mt-3 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
                >
                  {completed.loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
